/**
 * Entity types shared by the API and the web client.
 *
 * The source app kept two independent copies of these — `backend/src/types.ts`
 * and `frontend/src/types.ts` — which drifted from each other and from the
 * actual schema. This package is the single definition; that drift is the main
 * reason the port is a monorepo rather than two repos.
 *
 * These describe database rows. HTTP request/response DTOs live with the
 * feature module that owns them, because they are shaped by the endpoint
 * contract rather than by the table.
 *
 * Faithful to `schema.ts` rather than to the source's types.ts, which had gone
 * stale — divergences are called out inline.
 */
// ---------------------------------------------------------------------------
// Shared constants
// ---------------------------------------------------------------------------
/**
 * The palette a user can pick from for their profile circle, used when they
 * have no photo.
 *
 * Shared rather than duplicated because it is enforced on **both** sides:
 * `PUT /api/users/:userId/profile` rejects anything outside this list with
 * 400 'Invalid avatar color.', and the web client renders the same swatches.
 * The source kept two copies — `backend/src/utils/avatarColors.ts` and
 * `frontend/src/avatarColors.ts` — which is the pattern this package exists to
 * end. The values and their order are part of the API contract.
 */
export const AVATAR_COLORS = [
    '#E91E63',
    '#3F51B5',
    '#009688',
    '#FF5722',
    '#9C27B0',
    '#4CAF50',
    '#FF9800',
    '#607D8B',
    '#795548',
    '#00BCD4',
    '#F06292',
    '#7986CB',
];
export const isValidAvatarColor = (color) => typeof color === 'string' &&
    AVATAR_COLORS.includes(color);
/**
 * How many gallery photos a user may keep on their profile, beyond the two
 * then/now portraits.
 *
 * Shared for the same reason as `AVATAR_COLORS`: it is enforced on **both**
 * sides. `POST /api/users/:userId/gallery` is the real gate — it 400s with
 * 'Gallery limit of N photos reached.' — while the web client hides the upload
 * control and renders the `n/N` counter from the same number. It was previously
 * written out in eight places across the two apps, so raising it meant finding
 * every one; the API's error string and the client's are both built from this
 * constant now, which is what keeps them saying the same thing.
 */
export const GALLERY_LIMIT = 25;
//# sourceMappingURL=index.js.map